import React, { useCallback, useEffect, useState } from 'react';
import { v2Api, type Phase, type PhasePayload } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Pencil, Trash2, Loader2, Layers } from 'lucide-react';

const textareaClass =
  'flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

/** Parse API error response: standard { detail, errors } or legacy flat keys. */
function formatApiError(data: unknown, fallbackErr: unknown): string {
  if (!data || typeof data !== 'object') {
    return fallbackErr instanceof Error ? fallbackErr.message : 'Failed to save phase.';
  }
  const d = data as Record<string, unknown>;
  const parts: string[] = [];
  if (typeof d.detail === 'string') parts.push(d.detail);
  if (d.errors && typeof d.errors === 'object' && !Array.isArray(d.errors)) {
    const errors = d.errors as Record<string, unknown>;
    for (const [field, messages] of Object.entries(errors)) {
      if (Array.isArray(messages)) {
        parts.push(`${field}: ${(messages as string[]).join(', ')}`);
      }
    }
  }
  if (parts.length > 0) return parts.join(' — ');
  for (const key of Object.keys(d)) {
    if (key === 'detail' || key === 'errors') continue;
    const v = d[key];
    if (Array.isArray(v)) parts.push((v as string[]).join(', '));
    else if (typeof v === 'string') parts.push(v);
  }
  if (parts.length > 0) return parts.join(' — ');
  return fallbackErr instanceof Error ? fallbackErr.message : 'Failed to save phase.';
}

export default function PhasesPage() {
  const [phases, setPhases] = useState<Phase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<PhasePayload>({ name: '', order: 1, slug: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingPhase, setDeletingPhase] = useState<Phase | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchPhases = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await v2Api.dashboard.phases.list();
      setPhases(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load phases');
      setPhases([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPhases();
  }, [fetchPhases]);

  const openCreate = () => {
    setSaveError(null);
    setEditingId(null);
    setForm({
      name: '',
      order: phases.length > 0 ? Math.max(...phases.map((p) => p.order)) + 1 : 1,
      slug: '',
      description: '',
    });
    setDialogOpen(true);
  };

  const openEdit = (phase: Phase) => {
    setSaveError(null);
    setEditingId(phase.id);
    setForm({
      name: phase.name,
      order: phase.order,
      slug: phase.slug,
      description: phase.description ?? '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    setSaveError(null);
    try {
      if (editingId != null) {
        await v2Api.dashboard.phases.update(editingId, form);
      } else {
        await v2Api.dashboard.phases.create(form);
      }
      setDialogOpen(false);
      fetchPhases();
    } catch (err: unknown) {
      const data = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: unknown } }).response?.data
        : null;
      setSaveError(formatApiError(data, err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    setDeletingPhase(phases.find((p) => p.id === id) ?? null);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!deletingPhase) return;
    setIsDeleting(true);
    try {
      await v2Api.dashboard.phases.delete(deletingPhase.id);
      setDeleteDialogOpen(false);
      setDeletingPhase(null);
      fetchPhases();
      toast.success('Phase deleted successfully.');
    } catch (err) {
      console.error('Delete phase failed:', err);
      toast.error('Failed to delete phase.');
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <Layers className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
              Phases
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage TTX phases. Assign phases to injects when editing a scenario&apos;s injects.
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-center text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {phases.length} phase{phases.length !== 1 ? 's' : ''}
        </p>
        <Button onClick={openCreate} size="sm">
          <Plus className="mr-2 h-4 w-4" />
          Add Phase
        </Button>
      </div>

      <div className="space-y-3">
        {phases.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              No phases yet. Add one to use in scenario injects.
            </CardContent>
          </Card>
        ) : (
          phases
            .slice()
            .sort((a, b) => a.order - b.order)
            .map((phase) => (
              <Card key={phase.id}>
                <CardHeader className="py-3 px-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-base flex items-center gap-2">
                        <span className="text-muted-foreground font-normal">#{phase.order}</span>
                        {phase.name}
                      </CardTitle>
                      {phase.description && (
                        <CardDescription className="mt-1 line-clamp-2">{phase.description}</CardDescription>
                      )}
                      <span className="text-xs text-muted-foreground mt-1 inline-block">{phase.slug}</span>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(phase)} aria-label="Edit">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(phase.id)}
                        aria-label="Delete"
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>{editingId != null ? 'Edit Phase' : 'Add Phase'}</DialogTitle>
            <DialogDescription>Phases can be assigned to injects in scenario TTX settings.</DialogDescription>
          </DialogHeader>
          {saveError && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive" role="alert">
              {saveError}
            </div>
          )}
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="phase-name">Name</Label>
              <Input
                id="phase-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Briefing"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="phase-slug">Slug (optional, auto-generated from name if empty)</Label>
              <Input
                id="phase-slug"
                value={form.slug}
                onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                placeholder="e.g. briefing"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="phase-order">Order</Label>
              <Input
                id="phase-order"
                type="number"
                min={1}
                value={form.order ?? 1}
                onChange={(e) => setForm((f) => ({ ...f, order: parseInt(e.target.value, 10) || 1 }))}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="phase-description">Description (optional)</Label>
              <textarea
                id="phase-description"
                className={textareaClass}
                value={form.description ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Phase description"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving || !form.name.trim()}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : editingId != null ? (
                'Update'
              ) : (
                'Create'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setDeleteDialogOpen(false);
          setDeletingPhase(null);
        }
      }}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Delete phase?</DialogTitle>
            <DialogDescription>
              This action cannot be undone. Are you sure you want to delete{' '}
              <strong>{deletingPhase?.name}</strong>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={isDeleting}>
              {isDeleting ? 'Deleting...' : 'Delete Phase'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
